//side bar 2
import React, { useContext, useState, useEffect, useRef,useCallback } from "react";
import { Link } from "react-router-dom";
import { buildNotificationMessage, resolveSenderNames, extractNotificationData } from "../utils/notificationUtils";
import { apiFetch } from "../context/AuthContext";

import {
  X,
  Menu,
  Search,
  Bell,
  LogOut,
  Sun,
  Moon,
  User,
  Mail,
  Clock,
  CheckCircle,
  Calendar,
  MessageSquare,
  Send,
  Settings,
  Shield,
  Edit3,
  Camera,
  ChevronDown,
  Phone,
  MapPin,
  Code,
  ExternalLink,
  MoveLeft,
  FileText,
  ChevronLeft,
  ChevronRight,
  Power,
  RefreshCcw,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { tr } from "../utils/i18n";
import logo from "../Images/photos/icon.png";
import logo1 from "../Images/photos/icon121.png";
//import logoutVideo from "./photos/ww.mp4";
import { useNavigation } from "../constant/NavigationContext";
import { useModal } from "../constant/ModalContext";
//import logoutVideo from "./photos/ww.mp4";
import s4Image from "../Images/photos/s4.png";
import s3Image from "../Images/photos/s11.png";
//import s5Image from "./photos/s3.png";
import { AlertCircle } from "lucide-react";
import LeaveRequestModal from "./leaveModal";
import { LanguageContext } from "./LanguageContext";
import AlertModal from "../components/AlertModal";
import { useLogout } from "../hooks/useLogout";
import { useProfile } from "../hooks/useProfile";

// ===== RESPONSIVE HELPER FUNCTIONS =====

const getResponsiveBadgeSize = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth <= 480) return 12;
  if (screenWidth <= 768) return 14;
  if (screenWidth <= 1024) return 15;
  return 16;
};

const getResponsiveSidebarWidth = (isMobile, isTablet, isCollapsed) => {
  if (isMobile) return isCollapsed ? "0" : "85vw";
  if (isTablet) return isCollapsed ? "4.5rem" : "18rem";
  return isCollapsed ? "4.5rem" : "17.5rem";
};

const getResponsiveFontSizes = (isMobile, isTablet) => {
  return {
    title: isMobile ? "1.125rem" : isTablet ? "1.25rem" : "1.375rem",
    body: isMobile ? "0.875rem" : isTablet ? "0.9375rem" : "0.9375rem",
    small: isMobile ? "0.75rem" : isTablet ? "0.8125rem" : "0.8125rem",
    tiny: isMobile ? "0.6875rem" : "0.75rem",
  };
};

const getResponsiveSpacing = (isMobile, isTablet) => {
  return {
    padding: isMobile ? "0.75rem" : isTablet ? "1rem" : "1.25rem",
    margin: isMobile ? "0.5rem" : isTablet ? "0.75rem" : "1rem",
    gap: isMobile ? "0.5rem" : isTablet ? "0.75rem" : "1rem",
  };
};

// ===== TOOLTIP COMPONENT FOR MINI SIDEBAR =====
const Tooltip = ({ children, text, show, position = "right" }) => {
  if (!show || !text) return children;

  return (
    <div
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      {children}
      <div
        style={{
          position: "absolute",
          left: position === "right" ? "120%" : "auto",
          right: position === "left" ? "120%" : "auto",
          top: "50%",
          transform: "translateY(-50%)",
          background: "rgba(0, 0, 0, 0.9)",
          color: "white",
          padding: "0.75rem 1rem",
          borderRadius: "0.75rem",
          fontSize: "0.875rem",
          fontWeight: "500",
          whiteSpace: "nowrap",
          zIndex: 10000,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          opacity: 1,
          visibility: "visible",
          transition: "all 0.2s ease",
          pointerEvents: "none",
        }}
      >
        {text}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: position === "right" ? "-6px" : "auto",
            right: position === "left" ? "-6px" : "auto",
            transform: "translateY(-50%) rotate(45deg)",
            width: "12px",
            height: "12px",
            background: "rgba(0, 0, 0, 0.9)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRight:
              position === "right"
                ? "none"
                : "1px solid rgba(255, 255, 255, 0.1)",
            borderBottom:
              position === "right"
                ? "none"
                : "1px solid rgba(255, 255, 255, 0.1)",
            borderLeft:
              position === "left"
                ? "none"
                : "1px solid rgba(255, 255, 255, 0.1)",
            borderTop:
              position === "left"
                ? "none"
                : "1px solid rgba(255, 255, 255, 0.1)",
          }}
        />
      </div>
    </div>
  );
};

const SidebarWithTopbar = ({
  navItems,
  currentPage,
  handlePageChange,
  user,
  notifications,
  handleLogout,
  handleSwitchManager,
  sidebarOpen,
  setSidebarOpen,
  children,
  themeToggle,
  viewModeSwitcher,
}) => {
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext); // Get current language
  const isRTL = language === "ar"; // Arabic is RTL
  // const isRTL = false; // Force LTR for testing
  const nav = useNavigation();
  const page =
    typeof handlePageChange === "function"
      ? currentPage
      : nav?.page ?? currentPage;
  const setPage =
    typeof handlePageChange === "function"
      ? handlePageChange
      : nav?.setPage ?? (() => {});
  const { showModal } = useModal();
  const { profile, profileLoading } = useProfile();

  // ===== CHECK USER ROLE =====
  const getUserRole = () => {
    return profile?.role || 'user';
  };

  const isManagerRole = () => {
    const role = getUserRole();
    return role === 'manager' || role === 'superadmin';
  };

  // ===== TRANSLATIONS =====
  const t = {
    en: {
      dashboard: "Dashboard",
      employees: "Employees",
      attendance: "Attendance",
      calendar: "Calendar",
      reports: "Reports",
      settings: "Settings",
      poweredBy: "Powered by",
      notifications: "Notifications",
      markAllRead: "Mark All Read",
      logout: "Logout",
      managerView: "Manager ",
      profile: "Profile",
      contact: "Contact",
      noEmail: "No email",
      noPhone: "No phone",
    },
    ar: {
      dashboard: "لوحة التحكم",
      employees: "الموظفين",
      attendance: "الحضور",
      calendar: "التقويم",
      reports: "التقارير",
      settings: "الإعدادات",
      poweredBy: "مدعوم من",
      notifications: "الإشعارات",
      markAllRead: "تحديد الكل كمقروء",
      logout: "تسجيل الخروج",
      managerView: "عرض المدير",
      profile: "الملف الشخصي",
      contact: "التواصل",
      noEmail: "لا يوجد بريد إلكتروني",
      noPhone: "لا يوجد رقم هاتف",
    },
  }[language];
  // ===== STATE MANAGEMENT =====
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [notificationSize, setNotificationSize] = useState("large");
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [notificationComments, setNotificationComments] = useState({});
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showCalendarEventModal, setShowCalendarEventModal] = useState(false);
const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null);
  const [selectedLeaveNotification, setSelectedLeaveNotification] =
    useState(null);

 // const [showLogoutVideo, setShowLogoutVideo] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // ===== ALERT MODAL STATE =====
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const showAlert = (title, message, variant = 'info') => {
    setAlertModal({ open: true, title, message, variant });
  };
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, open: false }));
  };

  // ===== MINI SIDEBAR STATE =====
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);

  // Responsive breakpoint states
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth <= 1024 && window.innerWidth > 768
  );
  const [isSmallLaptop, setIsSmallLaptop] = useState(
    window.innerWidth <= 1366 && window.innerWidth > 1024
  );
  const [isLargeLaptop, setIsLargeLaptop] = useState(
    window.innerWidth <= 1920 && window.innerWidth > 1366
  );
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1920);

  // Dynamic responsive values
  const [badgeSize, setBadgeSize] = useState(getResponsiveBadgeSize());
  const [sidebarWidth, setSidebarWidth] = useState(
    getResponsiveSidebarWidth(isMobile, isTablet, isCollapsed)
  );
  const [fontSizes, setFontSizes] = useState(
    getResponsiveFontSizes(isMobile, isTablet)
  );
  const [spacing, setSpacing] = useState(
    getResponsiveSpacing(isMobile, isTablet)
  );

  // ===== REFS =====
  const notificationRef = useRef(null);
  const userProfileRef = useRef(null);
  const sidebarRef = useRef(null);
  const commentModalRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
const senderCacheRef = useRef({}); // cache resolved sender names by id
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
  const profilePicInputRef = useRef(null);
  const { logout } = useLogout();

  const handleLogoutClick = () => {
    setShowUserProfile(false);
    setShowLogoutModal(true);
  };

  const completeLogout = async () => {
    setShowLogoutModal(false);
    await logout(); // ← this calls your hook which hits the backend + clears session
  };

  // ===== RESPONSIVE SCREEN SIZE DETECTION =====
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      setIsMobile(width <= 768);
      setIsTablet(width <= 1024 && width > 768);
      setIsSmallLaptop(width <= 1366 && width > 1024);
      setIsLargeLaptop(width <= 1920 && width > 1366);
      setIsDesktop(width > 1920);

      setBadgeSize(getResponsiveBadgeSize());
      setSidebarWidth(
        getResponsiveSidebarWidth(
          width <= 768,
          width <= 1024 && width > 768,
          isCollapsed
        )
      );
      setFontSizes(
        getResponsiveFontSizes(width <= 768, width <= 1024 && width > 768)
      );
      setSpacing(
        getResponsiveSpacing(width <= 768, width <= 1024 && width > 768)
      );

      if (width <= 768 && isCollapsed) {
        setIsCollapsed(false);
      }

      if (width <= 480) {
        setNotificationSize("small");
      } else if (width <= 768) {
        setNotificationSize("medium");
      } else {
        setNotificationSize("large");
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [isCollapsed, sidebarOpen]);

  // ===== DATE TIME UPDATE =====
  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // ===== NOTIFICATION DATA =====
  const [notificationData, setNotificationData] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
// ===== FORMAT TIME AGO =====
const formatTimeAgo = useCallback((dateString, lang = language) => {
  const now = new Date();
  const notificationDate = new Date(dateString);
  const diffInMilliseconds = now - notificationDate;
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));

  if (lang === "ar") {
    if (diffInMinutes < 1) return "الآن";
    if (diffInMinutes === 1) return "منذ دقيقة";
    if (diffInMinutes < 60) return `منذ ${diffInMinutes} دقائق`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return "منذ ساعة";
    if (diffInHours < 24) return `منذ ${diffInHours} ساعات`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "منذ يوم";
    if (diffInDays < 7) return `منذ ${diffInDays} أيام`;

    const months = [
      "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
      "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
    ];
    const day = notificationDate.getDate();
    const month = months[notificationDate.getMonth()];
    const year = notificationDate.getFullYear();
    return `${day} ${month} ${year}`;
  }

  // English (default)
  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes === 1) return "1 minute ago";
  if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours === 1) return "1 hour ago";
  if (diffInHours < 24) return `${diffInHours} hours ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return "1 day ago";
  if (diffInDays < 7) return `${diffInDays} days ago`;

  const monthsEn = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const day = notificationDate.getDate();
  const month = monthsEn[notificationDate.getMonth()];
  const year = notificationDate.getFullYear();
  return `${month} ${day}, ${year}`;
}, [language]);

// ===== FETCH NOTIFICATIONS =====

const fetchNotifications = useCallback(async () => {
  setNotificationsLoading(true);
  setNotificationsError(null);

  try {
    const infinityCode = profile?.infinityCode || localStorage.getItem('infinityCode');
    if (!infinityCode) {
      throw new Error("No infinity code found");
    }

    const response = await apiFetch(
      `${BASE_URL}/calendar/notifications/${infinityCode}`,
      { method: 'GET' }
    );


    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("Raw notification data:", result);

    if (result.success && result.data) {
      const transformedData = result.data.map((notification) => {
        const extracted = extractNotificationData(notification);
        
        const idStr = extracted.from ? String(extracted.from) : null;
          const cachedNameObj = idStr ? senderCacheRef.current[idStr] : null;
          const senderName = cachedNameObj
            ? (language === "ar" && cachedNameObj.ar ? cachedNameObj.ar : cachedNameObj.en || cachedNameObj.ar)
            : (extracted.from || (language === "ar" ? "مستخدم" : "User"));
          const senderForMessage = (language === 'ar' && cachedNameObj && cachedNameObj.en) ? cachedNameObj.en : senderName;

          const message = buildNotificationMessage(extracted, senderForMessage, language);
        
        let icon = Calendar;
        if (extracted.type === 'LEAVE_REQUEST') icon = FileText;
        else if (extracted.type === 'MESSAGE') icon = MessageSquare;
        else if (extracted.type === 'DOCUMENT_REQUEST') icon = FileText;
        
        let title = message || extracted.eventTitle || extracted.originalMessage || '';
        if (extracted.type && extracted.type.startsWith && extracted.type.startsWith('LEAVE')) {
          title = language === 'ar' ? "طلب إجازة" : "Leave Request";
        } else if (extracted.type && extracted.type.startsWith && extracted.type.startsWith('ADVANCEMENT')) {
          const src = (extracted.eventTitle || extracted.originalMessage || '').toLowerCase();
          if (/reject|rejected|رفض/.test(src)) {
            title = language === 'ar' ? 'تم رفض طلب السلفه' : 'Advancement Request Rejected';
          } else if (/approve|approved|مواف/.test(src)) {
            title = language === 'ar' ? 'الموافقة على طلب سلفة' : 'Advancement Request Approved';
          } else if (/disburs|disbursed|صرف/.test(src)) {
            title = language === 'ar' ? 'تم صرف السلفة' : 'Advancement Disbursed';
          } else {
            title = language === 'ar' ? 'طلب سلفة' : 'Advancement Request';
          }
        } else if (extracted.type === 'CALENDAR_EVENT') {
            const calSrc = (extracted.originalMessage || '').toLowerCase();
            if (/declin|رفض/.test(calSrc)) {
              title = language === 'ar' ? 'رفض دعوة الحدث' : 'Event Invitation Declined';
            } else if (/accept|confirm|قبل|وافق/.test(calSrc)) {
              title = language === 'ar' ? 'قبول دعوة الحدث' : 'Event Invitation Accepted';
            } else {
              title = extracted.eventTitle || (language === 'ar' ? 'دعوة لحدث' : 'Event Invitation');
            }
          } else if (extracted.type === 'DOCUMENT_REQUEST' || extracted.eventTitle === 'Document Request' || extracted.eventTitle === 'طلب مستند') {
            title = language === 'ar' ? 'طلب مستند' : 'Document Request';
        } else if (title && title.length > 50) {
          title = title.substring(0, 47) + "...";
        }

        return {
          id: extracted.id,
          from: extracted.from,
          fromName: senderName,
          type: extracted.type,
          title: title,
          message: message,
          read: extracted.read,
          icon: icon,
          eventId: extracted.eventId,
          leaveId: extracted.leaveId,
          documentId: extracted.documentId,
          advancementId: extracted.advancementId,
          changeId: extracted.changeId,
          messageId: extracted.messageId,
          eventTitle: extracted.eventTitle,
          leaveType: extracted.leaveType,
          documentType: extracted.documentType,
          createdAt: extracted.createdAt,
          originalMessage: extracted.originalMessage,
        };
      });

      const containsArabic = (s) => /[\u0600-\u06FF]/.test(s || '');
      const filteredData = transformedData.filter((n) => {
        const text = n.message || n.originalMessage || '';
        const hasAr = containsArabic(text);
        if (n.type === 'DOCUMENT_REQUEST') return true;
        return language === 'ar' ? hasAr : !hasAr;
      });
      setNotificationData(filteredData.length > 0 ? filteredData : transformedData);

      // Resolve sender names for numeric sender IDs
      const idsToResolve = Array.from(
        new Set(
          transformedData
            .map((n) => (n.from ? String(n.from) : null))
            .filter((v) => v && /^\d+$/.test(v) && !senderCacheRef.current[v])
        )
      );

      if (idsToResolve.length > 0) {
        const authFetchFn = (url) => apiFetch(url, { method: 'GET' });
        try {
          await resolveSenderNames(idsToResolve, senderCacheRef, BASE_URL, authFetchFn);
          setNotificationData((prev) =>
            prev.map((n) => {
              const idStr = n.from ? String(n.from) : null;
              const nameObj = idStr ? senderCacheRef.current[idStr] : null;
              const name = nameObj 
                ? (language === 'ar' && nameObj.ar ? nameObj.ar : nameObj.en || nameObj.ar)
                : (n.from || (language === 'ar' ? "مستخدم" : "User"));
              return { ...n, fromName: name, message: buildNotificationMessage(n, name, language) };
            })
          );
        } catch (e) {
          console.error("Error resolving sender names:", e);
        }
      }
    } else {
      throw new Error("Invalid response format");
    }
  } catch (error) {
    console.error("Error fetching notifications:", error);
    setNotificationsError(error.message);
    setNotificationData([]);
  } finally {
    setNotificationsLoading(false);
  }
}, [BASE_URL, language, formatTimeAgo]);



 useEffect(() => {
  if (profileLoading) return;
  fetchNotifications();
  const interval = setInterval(fetchNotifications, 30000);
  return () => clearInterval(interval);
}, [fetchNotifications, profileLoading]);

  // Rebuild messages when language changes
  useEffect(() => {
    if (notificationData.length === 0) return;
    setNotificationData((prev) =>
      prev.map((n) => {
        const idStr = n.from ? String(n.from) : null;
        const nameObj = idStr ? senderCacheRef.current[idStr] : null;
        const name = nameObj ? (language === 'ar' && nameObj.ar ? nameObj.ar : nameObj.en || nameObj.ar) : (n.fromName || n.from || (language === 'ar' ? "مستخدم" : "User"));
        return { ...n, fromName: name, message: buildNotificationMessage(n, name, language) };
      })
    );
  }, [language]);

  // Re-format notification times when language changes
 

  // ===== PROFILE PICTURE =====
  const [profilePic, setProfilePic] = useState(null);

  useEffect(() => {
  async function fetchProfilePic() {
    try {
      const infinityCode = profile?.infinityCode;
      const response = await apiFetch(
        `${BASE_URL}/employees/pic/${infinityCode}`,
        { method: 'GET' }
      );

        if (!response.ok) return;

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setProfilePic(url);
      } catch (err) {
        console.error("Error loading profile pic:", err);
      }
    }

    fetchProfilePic();
  }, []);

  // ===== USER DATA =====
  const userData = {
    name:
      language === "ar"
        ? profile?.employeeArabicName ||
          profile?.employeeName ||
          "أحمد حسن"
        : profile?.employeeName || "Ahmed Hassan",
    username: user.username || "ahmed.hassan",
    email: profile?.employeeEmail,
    phone: profile?.employeePhone,
    avatar: profilePic || "/default-avatar.png",
    status: "online",
    lastSeen: "2 minutes ago",
    permissions: ["admin", "developer", "team_lead"],
  };

  const unreadCount = notificationData.filter((n) => !n.read).length;

  // ===== UTILITY FUNCTIONS =====
  const formatDateTime = (date) => {
    const options = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
    return date.toLocaleDateString("en-US", options);
  };

  // ===== CLICK OUTSIDE HANDLERS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        commentModalRef.current &&
        !commentModalRef.current.contains(event.target)
      ) {
        setShowCommentModal(false);
        setSelectedNotification(null);
        setCommentText("");
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }

      if (
        userProfileRef.current &&
        !userProfileRef.current.contains(event.target)
      ) {
        setShowUserProfile(false);
      }

      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const menuButton = event.target.closest(".menu-btn");
        const collapseButton = event.target.closest(".collapse-btn");
        if (!menuButton && !collapseButton && sidebarOpen && isMobile) {
          setSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [sidebarOpen, setSidebarOpen, isMobile]);

  // ===== KEYBOARD HANDLERS =====
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        if (showLeaveModal) {
          setShowLeaveModal(false);
          setSelectedLeaveNotification(null);
        } else if (showCommentModal) {
          setShowCommentModal(false);
          setSelectedNotification(null);
          setCommentText("");
        } else if (showNotifications) {
          setShowNotifications(false);
        } else if (showUserProfile) {
          setShowUserProfile(false);
        } else if (sidebarOpen) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [
    sidebarOpen,
    showNotifications,
    showUserProfile,
    showCommentModal,
    showLeaveModal,
    setSidebarOpen,
  ]);

  // ===== EVENT HANDLERS =====
  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const toggleNotifications = (event) => {
    event.stopPropagation();
    setShowNotifications(!showNotifications);
    setShowUserProfile(false);
  };

  const toggleUserProfile = (event) => {
    event.stopPropagation();
    setShowUserProfile(!showUserProfile);
    setShowNotifications(false);
  };

  const handleSidebarToggle = (event) => {
    event.stopPropagation();
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleNotificationClick = (event, id) => {
    event.stopPropagation();
    markAsRead(id);

    const notification = notificationData.find(n => n.id === id);
    if (!notification) return;

    window.__notificationTarget = {
      leaveId: notification.leaveId || null,
      actionId: notification.actionId || null,
      advancementId: notification.advancementId || null,
      documentId: notification.documentId || null,
      type: notification.type,
    };

    if (notification.type === "CALENDAR_EVENT") {
      setPage("calendar");
    } else if (
      notification.type === "LEAVE_REQUEST" ||
      notification.type === "LEAVE_STATUS_UPDATE" ||
      notification.title === "New Leave Request"
    ) {
      setPage("LeaveApprovalPage");
    } else if (notification.type === "EMPLOYEE_ACTION") {
      setPage("ChangesPage");
    } else if (notification.type && notification.type.startsWith("ADVANCEMENT")) {
      setPage("Mo3tiAdvancementManagement");
    } else if (notification.type === "DOCUMENT_REQUEST") {
      setPage("Mo3tiAdminDocumentRequests");
    }
    setShowNotifications(false);
  };

  // Handle RSVP directly from notification items (accept/decline/going/maybe)
  const handleNotificationRsvp = async (e, notification, status) => {
    e.stopPropagation();
    try {
      const infinityCode = profile?.infinityCode;
      if (!infinityCode) throw new Error("No user session");

      const response = await apiFetch(
        `${BASE_URL}/events/${notification.eventId}/rsvp/${infinityCode}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        }
      );

      const data = await response.json().catch(() => ({}));
      // Refresh notifications so the item and counts update
      await fetchNotifications();
      // Optionally close the notifications panel
      setShowNotifications(false);
      if (!response.ok) {
        // show backend message if provided
        const msg = data?.message || 'Failed to send response';
        setNotificationsError(msg);
      }
    } catch (err) {
      console.error('RSVP from notification failed:', err);
      setNotificationsError(err.message || 'Failed to send response');
    }
  };

  const markAsRead = async (notificationId) => {
  try {
    const response = await apiFetch(
      `${BASE_URL}/calendar/notifications/${notificationId}/read`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      }
    );


      if (response.ok) {
        setNotificationData((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        );
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };
const markAllAsRead = async () => {
  try {
    const infinityCode = profile?.infinityCode;

    if (!infinityCode) {
      throw new Error("No infinity code found");
    }

    const response = await apiFetch(
      `${BASE_URL}/calendar/notifications/${infinityCode}/readAll`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      }
    );


      if (response.ok) {
        setNotificationData((prev) =>
          prev.map((notification) => ({ ...notification, read: true }))
        );
      } else {
        console.error("Failed to mark all notifications as read");
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

const handleRedDotClick = (event, notification) => {
  event.stopPropagation();

  // Handle Calendar Event click
if (notification.type === "CALENDAR_EVENT") {
  (async () => {
    const idStr = notification.from ? String(notification.from) : null;
    if (idStr && /^\d+$/.test(idStr) && !senderCacheRef.current[idStr]) {
      try {
        const resp = await apiFetch(`${BASE_URL}/employees/select/${idStr}`, { method: 'GET' });

          if (resp.ok) {
            const data = await resp.json();
            const emp = data.employee || data.data || data;
            const enName = emp?.fullName || emp?.englishName || emp?.employeeName || emp?.name || emp?.sr || "";
            const arName = emp?.arabicName || emp?.arabic_name || emp?.arabicFullName || emp?.nameArabic || "";
            senderCacheRef.current[idStr] = { en: enName || arName || idStr, ar: arName || enName || idStr };
          }
        } catch (e) {
          console.error("Failed resolving sender for calendar event", e);
        }
      }

      const displayNameObj = notification.from ? senderCacheRef.current[String(notification.from)] : null;
      // Get sender name with proper language support - use Arabic if available
      const displayName = displayNameObj 
        ? (language === 'ar' && displayNameObj.ar ? displayNameObj.ar : displayNameObj.en || displayNameObj.ar) 
        : notification.from;

      setSelectedCalendarEvent({ ...notification, fromName: displayName });
      setShowCalendarEventModal(true);
      markAsRead(notification.id);
    })();
  }
  // Handle Leave Request click
  else if (
    notification.type === "LEAVE_REQUEST" ||
    notification.title === "New Leave Request"
  ) {
    const currentUserInfinityCode = profile?.infinityCode;
    const userRole = profile?.role;

    const isAdmin = userRole && userRole.toLowerCase() !== "user";
    const isRecipient =
      currentUserInfinityCode &&
      notification.infinityCode &&
      String(currentUserInfinityCode) ===
      String(notification.infinityCode);

    if (isAdmin && isRecipient) {
      setSelectedLeaveNotification(notification);
      setShowLeaveModal(true);
    } else {
      markAsRead(notification.id);
    }
  }
  // Handle other notification types
  else {
    setSelectedNotification(notification);
    setShowCommentModal(true);
    setCommentText("");
  }
};

  // ===== COMMENT HANDLERS =====
  const handleCommentSubmit = () => {
    if (commentText.trim() && selectedNotification) {
      const newComment = {
        id: Date.now(),
        text: commentText.trim(),
        timestamp: new Date().toISOString(),
        author: userData.username,
      };

      setNotificationComments((prev) => ({
        ...prev,
        [selectedNotification.id]: [
          ...(prev[selectedNotification.id] || []),
          newComment,
        ],
      }));

      setCommentText("");
      setShowCommentModal(false);
      setSelectedNotification(null);
    }
  };

  const handleCommentKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleCommentSubmit();
    }
  };

  const handleProfilePicSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleProfilePicUpload(file);
    }
  };

  const handleProfilePicUpload = async (file) => {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      showAlert(t.error || "Error", language === 'ar' ? "يرجى اختيار ملف صورة" : "Please select an image file", 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showAlert(t.error || "Error", language === 'ar' ? "حجم الملف يجب أن يكون أقل من 5 ميجابايت" : "File size must be less than 5MB", 'error');
      return;
    }

    setIsUploadingProfilePic(true);

    try {
      const formData = new FormData();
      formData.append("profilePicture", file);
      const infinityCode = profile?.infinityCode;

      const response = await apiFetch(
        `${BASE_URL}/employees/pic/load/${infinityCode}`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
  // Reload profile picture
  const imageResponse = await apiFetch(
    `${BASE_URL}/employees/pic/${infinityCode}`,
    { method: 'GET' }
  );

        if (imageResponse.ok) {
          const blob = await imageResponse.blob();
          const url = URL.createObjectURL(blob);
          setProfilePic(url);
        }
        showAlert(t.success || "Success", language === 'ar' ? "تم تحديث صورة الملف الشخصي بنجاح!" : "Profile picture updated successfully!", 'success');
      } else {
        const errorText = await response.text();
        console.error("Upload failed:", response.status, errorText);
        showAlert(t.error || "Error", language === 'ar' ? `فشل التحميل: ${response.status} - ${errorText}` : `Upload failed: ${response.status} - ${errorText}`, 'error');
      }
    } catch (error) {
      console.error("Network error:", error);
      showAlert(t.error || "Error", language === 'ar' ? `خطأ في الشبكة: ${error.message}` : `Network error: ${error.message}`, 'error');
    } finally {
      setIsUploadingProfilePic(false);
    }
  };

  const triggerProfilePicInput = () => {
    profilePicInputRef.current?.click();
  };

  // ===== NOTIFICATION SIZES =====
  const getNotificationSizes = () => {
    if (isMobile) {
      return {
        small: { width: "90vw", maxHeight: "60vh" },
        medium: { width: "90vw", maxHeight: "65vh" },
        large: { width: "90vw", maxHeight: "70vh" },
      };
    } else if (isTablet) {
      return {
        small: { width: "22rem", maxHeight: "18.75rem" },
        medium: { width: "26rem", maxHeight: "25rem" },
        large: { width: "28rem", maxHeight: "31.25rem" },
      };
    } else {
      return {
        small: { width: "20rem", maxHeight: "18.75rem" },
        medium: { width: "24rem", maxHeight: "25rem" },
        large: { width: "26rem", maxHeight: "31.25rem" },
      };
    }
  };

  // ===== COLOR SYSTEM =====
  const colors = {
    light: {
      //wrapperBg: "`url(${s3Image}) no-repeat center center / cover fixed`",
      wrapperBg: "#f1f1f1ff",

      sidebarBg: "rgba(255, 255, 255, 0.95)",
      sidebarBorder: "rgba(0, 0, 0, 0.08)",
      text: "#0f172a",
      textSecondary: "#64748b",
      navHover: "rgba(59, 130, 246, 0.08)",
      navActive: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      topbarBg: "rgba(255, 255, 255, 0.95)",
      contentBg: "transparent",
      inputBg: "rgba(248, 250, 252, 0.8)",
      inputBorder: "rgba(0, 0, 0, 0.12)",
      shadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
      glassEffect: "blur(16px)",
      notificationBg: "rgba(255, 255, 255, 0.98)",
      notificationShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
      modalBg: "rgba(255, 255, 255, 0.98)",
      modalOverlay: "rgba(0, 0, 0, 0.4)",
      userProfileBg: "rgba(255, 255, 255, 0.98)",
      userProfileShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
      statusOnline: "#22c55e",
      statusOffline: "#94a3b8",
      statusAway: "#f59e0b",
      statusBusy: "#ef4444",
      scrollTrack: "rgba(249, 250, 251, 0.8)",
      scrollThumb:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(99, 102, 241, 0.2))",
      scrollThumbHover:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(99, 102, 241, 0.4))",
      scrollThumbActive:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.7), rgba(99, 102, 241, 0.6))",
    },
    dark: {
      //wrapperBg: `url(${s5Image}) no-repeat center center / cover fixed`,
      wrapperBg: "rgba(15, 23, 42, 0.95)",

      sidebarBg: "rgba(15, 23, 42, 0.95)",
      sidebarBorder: "rgba(255, 255, 255, 0.08)",
      text: "#ffffff",
      textSecondary: "rgba(255, 255, 255, 0.7)",
      navHover: "rgba(255, 255, 255, 0.08)",
      navActive: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      topbarBg: "rgba(15, 23, 42, 0.95)",
      contentBg: "transparent",
      inputBg: "rgba(255, 255, 255, 0.08)",
      inputBorder: "rgba(255, 255, 255, 0.12)",
      shadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
      glassEffect: "blur(16px)",
      notificationBg: "rgba(15, 23, 42, 0.98)",
      notificationShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
      modalBg: "rgba(15, 23, 42, 0.98)",
      modalOverlay: "rgba(0, 0, 0, 0.6)",
      userProfileBg: "rgba(15, 23, 42, 0.98)",
      userProfileShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
      statusOnline: "#22c55e",
      statusOffline: "#64748b",
      statusAway: "#f59e0b",
      statusBusy: "#ef4444",
      scrollTrack: "rgba(30, 41, 59, 0.6)",
      scrollThumb:
        "linear-gradient(135deg, rgba(99, 102, 241, 0.4), rgba(59, 130, 246, 0.3))",
      scrollThumbHover:
        "linear-gradient(135deg, rgba(99, 102, 241, 0.6), rgba(59, 130, 246, 0.5))",
      scrollThumbActive:
        "linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(59, 130, 246, 0.7))",
    },
  };

  const c = colors[theme];

  // ===== STYLES SYSTEM =====
  const getResponsiveStyles = () => {
    const sidebarWidthPx = isMobile
      ? sidebarOpen
        ? "85vw"
        : "0"
      : isCollapsed
      ? "4.5rem"
      : isTablet
      ? "18rem"
      : "17.5rem";

    const topbarHeight = isCollapsed
      ? "4.5rem"
      : isTablet
      ? "4.5rem"
      : "6.1rem";

    return {
      wrapper: {
        display: "flex",
        width: "100%",
        height: "100vh",
        background: c.wrapperBg,
        transition: "all 0.3s ease",
        overflow: "hidden",
        position: "fixed", // ? ??? ???
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },

      sidebar: {
        position: "fixed",
        left: isRTL ? "auto" : isMobile ? (sidebarOpen ? "0" : "-100%") : "0",
        right: isRTL
          ? isMobile
            ? sidebarOpen
              ? "0"
              : "-100%"
            : "0"
          : "auto",
        top: "0",
        width: sidebarWidthPx,
        height: "100vh",
        background: c.sidebarBg,
        backdropFilter: c.glassEffect,
        border: `1px solid ${c.sidebarBorder}`,
        borderLeft: isRTL ? undefined : "none",
        borderRight: isRTL ? "none" : undefined,
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        boxShadow: c.shadow,
        overflow: "hidden",
        direction: isRTL ? "rtl" : "ltr",
      },

      sidebarHeader: {
        minHeight: "90px",
        borderBottom: `1px solid ${c.sidebarBorder}`,
        display: "flex",
        alignItems: "center",
        // ? ??????? ??????? ???:
        justifyContent: "center",
        flexDirection: isRTL ? "row-reverse" : "row", // ? ??? ???
        gap: isCollapsed && !isMobile ? "0" : isRTL ? spacing.gap : "1.5rem", // ????? ????? ?? ?????????
        background:
          "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.05))",
        flexShrink: 0,
        padding: "1.5rem",
      },

      logo: {
        width:
          isCollapsed && !isMobile
            ? "2.5rem"
            : isMobile
            ? "2.5rem"
            : isTablet
            ? "2.75rem"
            : "3rem",
        height:
          isCollapsed && !isMobile
            ? "2.5rem"
            : isMobile
            ? "2.5rem"
            : isTablet
            ? "2.75rem"
            : "3rem",
        backgroundColor: "transparent",
      },

      sidebarTitle: {
        fontSize: fontSizes.title,
        fontWeight: "800",
        margin: 0,
        color: theme === "light" ? "#294f7c" : "#ffffff",
        letterSpacing: "0.03125rem",
        opacity: isCollapsed && !isMobile ? 0 : 1,
        transform:
          isCollapsed && !isMobile ? "translateX(-20px)" : "translateX(0)",
        transition: "all 0.3s ease",
        whiteSpace: "nowrap",
        overflow: "hidden",
        marginLeft: isRTL ? 0 : -10,
        marginRight: isRTL ? -10 : 0,
        marginTop: isMobile ? 0 : 18,
      },

      nav: {
        flex: 1,
        padding: `${spacing.margin} 0`,
        overflowY: "auto",
        overflowX: "hidden",
      },

      navItem: {
        display: "flex",
        alignItems: "center",
        justifyContent: isCollapsed && !isMobile ? "center" : "flex-start",
        gap:
          isCollapsed && !isMobile ? "0" : isMobile ? "0.75rem" : spacing.gap,
        padding:
          isCollapsed && !isMobile
            ? "1rem 0.3rem"
            : `${spacing.margin} ${spacing.padding}`,
        margin:
          isCollapsed && !isMobile
            ? `0 0.75rem 0.75rem 0.75rem`
            : `0 ${spacing.margin} 0.625rem ${spacing.margin}`,
        color: c.textSecondary,
        cursor: "pointer",
        fontSize: fontSizes.body,
        fontWeight: "500",
        borderRadius: isMobile ? "0.75rem" : "1rem",
        transition:
          isCollapsed && !isMobile
            ? "background 0.3s ease, color 0.3s ease"
            : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        border: "1px solid transparent",
        minHeight: "3rem",
        overflow: "hidden",
      },

      navItemActive: {
        background: c.navActive,
        color: "white",
        fontWeight: "600",
        transform:
          isCollapsed && !isMobile
            ? "scale(1.05)"
            : isMobile
            ? "translateX(0.25rem) scale(1.01)"
            : isTablet
            ? "translateX(0.5rem) scale(1.015)"
            : "translateX(0.5rem) scale(1.02)",
        boxShadow: "0 0.5rem 1.5625rem rgba(59, 130, 246, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      },

      navItemText: {
        opacity: isCollapsed && !isMobile ? 0 : 1,
        transform:
          isCollapsed && !isMobile ? "translateX(-20px)" : "translateX(0)",
        transition: "all 0.3s ease",
        whiteSpace: "nowrap",
        overflow: "hidden",
      },

      main: {
        flex: 1,
        marginLeft: isRTL ? "0" : isMobile ? "0" : sidebarWidthPx,
        marginRight: isRTL ? (isMobile ? "0" : sidebarWidthPx) : "0",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        direction: isRTL ? "rtl" : "ltr",
        position: "relative", // ? ??? ???
      },

      topbar: {
        background: c.topbarBg,
        backdropFilter: c.glassEffect,
        border: `1px solid ${c.sidebarBorder}`,
        borderRadius: isMobile ? "0" : "0 0 1.25rem 1.25rem",
        margin: isMobile ? "0" : "0 1rem",
        padding: isMobile
          ? `${spacing.padding} ${spacing.margin}`
          : isTablet
          ? `${spacing.padding} 1.5rem`
          : `${spacing.padding} 2rem`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        zIndex: 100,
        boxShadow:
          "0 4px 12px rgba(0, 0, 0, 0.03), 0 8px 24px rgba(59, 130, 246, 0.05), 0 16px 40px rgba(0, 0, 0, 0.02)",
        flexShrink: 0,
        minHeight: topbarHeight,
        direction: isRTL ? "rtl" : "ltr",
      },

      content: {
        flex: 1,
        background: c.contentBg,
        padding: isMobile ? spacing.margin : spacing.padding,
        color: c.text,
        transition: "all 0.3s ease",
        overflow: "auto",
        height: `calc(100vh - ${topbarHeight})`,
        position: "relative", // ? ??? ???
        paddingTop: isMobile ? "1rem" : "1.5rem", // ? ??? ????? ?? ???
      },

      topbarLeft: {
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "0.5rem" : spacing.gap,
        flex: "0 0 auto",
        order: 1,
        flexDirection: isRTL ? "row-reverse" : "row",
      },

      menuBtn: {
        background: c.inputBg,
        border: `1px solid ${c.inputBorder}`,
        color: c.text,
        cursor: "pointer",
        padding: isMobile ? "0.75rem" : "0.55rem",
        borderRadius: isMobile ? "0.75rem" : "8.875rem",
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(0.625rem)",
        boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
        flexShrink: 0,
      },

      topbarRight: {
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "0.5rem" : "1.125rem",
        color: c.text,
        flex: "0 0 auto",
        order: 2,
        flexDirection: isRTL ? "row-reverse" : "row",
      },

      themeToggle: {
        background: c.inputBg,
        border: `1px solid ${c.inputBorder}`,
        borderRadius: isMobile ? "0.75rem" : "0.875rem",
        padding: isMobile ? "0.75rem" : "0.75rem",
        cursor: "pointer",
        color: c.text,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease",
        backdropFilter: "blur(0.625rem)",
        boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
      },

      notificationWrapper: {
        position: "relative",
      },

      notificationBtn: {
        position: "relative",
        background: c.inputBg,
        border: `1px solid ${c.inputBorder}`,
        color: c.text,
        padding: isMobile ? "0.75rem" : "0.75rem",
        borderRadius: isMobile ? "0.75rem" : "0.875rem",
        cursor: "pointer",
        transition: "all 0.3s ease",
        backdropFilter: "blur(0.625rem)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
      },

      notificationBadge: {
        position: "absolute",
        top: "-0.125rem",
        right: "-0.125rem",
        background: "linear-gradient(135deg, #ef4444, #dc2626)",
        color: "white",
        borderRadius: "50%",
        border: "2px solid white",
        width: `${badgeSize}px`,
        height: `${badgeSize}px`,
        fontSize: `${badgeSize * 0.55}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "700",
        animation: "pulse 2s infinite",
        boxShadow: "0 0.25rem 0.75rem rgba(239, 68, 68, 0.4)",
      },

      notificationDropdown: {
        position: "absolute",
        top: "calc(100% + 0.75rem)",
        right: isRTL ? "auto" : isMobile ? "-1rem" : "0",
        left: isRTL ? (isMobile ? "-1rem" : "0") : "auto",
        ...getNotificationSizes()[notificationSize],
        background: c.notificationBg,
        backdropFilter: "blur(1.25rem)",
        border: `1px solid ${c.sidebarBorder}`,
        borderRadius: isMobile ? "1rem" : "1.25rem",
        boxShadow: c.notificationShadow,
        zIndex: 1001,
        opacity: showNotifications ? 1 : 0,
        visibility: showNotifications ? "visible" : "hidden",
        transform: showNotifications
          ? "translateY(0) scale(1)"
          : "translateY(-0.625rem) scale(0.95)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        direction: isRTL ? "rtl" : "ltr",
      },

      notificationHeader: {
        padding: isMobile ? "1rem 1.25rem" : "1.25rem 1.5rem",
        borderBottom: `1px solid ${c.sidebarBorder}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background:
          "linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(99, 102, 241, 0.02))",
      },

      notificationTitle: {
        fontSize: isMobile ? "1rem" : "1.125rem",
        fontWeight: "700",
        color: c.text,
      },

      notificationList: {
        maxHeight: isMobile ? "60vh" : "21.875rem",
        overflowY: "auto",
        padding: "0.75rem 0",
      },

      notificationItem: {
        padding: isMobile ? "0.875rem 1.25rem" : "1rem 1.5rem",
        borderBottom: `1px solid ${c.sidebarBorder}`,
        cursor: "pointer",
        transition: "all 0.2s ease",
        display: "flex",
        gap: isMobile ? "0.75rem" : "0.875rem",
        alignItems: "flex-start",
      },

      notificationIcon: {
        width: isMobile ? "2rem" : "2.25rem",
        height: isMobile ? "2rem" : "2.25rem",
        borderRadius: "0.75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      },

      notificationContent: {
        flex: 1,
        minWidth: 0,
      },

      notificationItemTitle: {
        fontSize: fontSizes.body,
        fontWeight: "600",
        color: c.text,
        marginBottom: "0.375rem",
      },

      notificationItemMessage: {
        fontSize: fontSizes.small,
        color: c.textSecondary,
        marginBottom: "0.375rem",
        lineHeight: "1.5",
      },

      notificationTime: {
        fontSize: fontSizes.tiny,
        color: c.textSecondary,
        fontWeight: "500",
      },

      unreadDot: {
        width: isMobile ? "0.75rem" : "0.875rem",
        height: isMobile ? "0.75rem" : "0.875rem",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #ef4444, #dc2626)",
        flexShrink: 0,
        marginTop: "0.375rem",
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "2px solid white",
        boxShadow: "0 0.25rem 0.75rem rgba(239, 68, 68, 0.4)",
      },

      userInfo: {
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "0.5rem" : "0.875rem",
        color: c.text,
        padding: isMobile ? "0.5rem 0.75rem" : "0.625rem 0.875rem",
        borderRadius: isMobile ? "0.75rem" : "0.875rem",
        transition: "all 0.3s ease",
        cursor: "pointer",
        background: c.inputBg,
        border: `1px solid ${c.inputBorder}`,
        backdropFilter: "blur(0.625rem)",
        boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
        position: "relative",
      },

      markAllReadBtn: {
        background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
        color: "white",
        border: "none",
        padding: isMobile ? "0.25rem 0.5rem" : "0.375rem 0.75rem",
        borderRadius: "0.5rem",
        cursor: "pointer",
        fontSize: fontSizes.tiny,
        fontWeight: "600",
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        transition: "all 0.3s ease",
        boxShadow: "0 0.125rem 0.5rem rgba(59, 130, 246, 0.3)",
      },

      userAvatar: {
        width: isMobile ? "1.75rem" : "2.625rem",
        height: isMobile ? "1.75rem" : "2.625rem",
        borderRadius: "50%",
        objectFit: "cover",
        border: "2px solid rgba(59, 130, 246, 0.3)",
        transition: "all 0.3s ease",
        position: "relative",
      },

      userAvatarStatus: {
        position: "absolute",
        bottom: "0.125rem",
        right: "0.125rem",
        width: isMobile ? "0.625rem" : "0.75rem",
        height: isMobile ? "0.625rem" : "0.75rem",
        borderRadius: "50%",
        border: "2px solid white",
        background: c.statusOnline,
      },

      userDetails: {
        display: isMobile ? "none" : "flex",
        flexDirection: "column",
        gap: "0.125rem",
      },

      userName: {
        fontSize: fontSizes.body,
        fontWeight: "600",
        color: c.text,
      },

      userDropdownIcon: {
        color: c.textSecondary,
        transition: "all 0.3s ease",
        transform: showUserProfile ? "rotate(180deg)" : "rotate(0deg)",
        display: isMobile ? "none" : "block",
      },

      userProfileDropdown: {
        position: "fixed",
        top: isMobile ? topbarHeight : "6.25rem",
        right: isRTL ? "auto" : isMobile ? "0.5rem" : "6.25rem",
        left: isRTL ? (isMobile ? "0.5rem" : "6.25rem") : "auto",
        width: isMobile
          ? "calc(100vw - 1rem)"
          : isTablet
          ? "25rem"
          : "31.25rem",
        background: c.userProfileBg,
        backdropFilter: "blur(1.25rem)",
        border: `1px solid ${c.sidebarBorder}`,
        borderRadius: isMobile ? "1rem" : "1.25rem",
        boxShadow: c.userProfileShadow,
        zIndex: 1001,
        opacity: showUserProfile ? 1 : 0,
        visibility: showUserProfile ? "visible" : "hidden",
        transform: showUserProfile
          ? "translateY(0) scale(1)"
          : "translateY(-0.625rem) scale(0.95)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        maxHeight: "80vh",
        overflowY: "auto",
        direction: isRTL ? "rtl" : "ltr",
      },

      userProfileHeader: {
        padding: isMobile ? "1.25rem" : "1.5rem",
        borderBottom: `1px solid ${c.sidebarBorder}`,
        background:
          "linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(99, 102, 241, 0.02))",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      },

      userProfileAvatar: {
        width: isMobile ? "4rem" : "5.75rem",
        height: isMobile ? "4rem" : "5.75rem",
        borderRadius: "50%",
        objectFit: "cover",
        border: "2px solid rgba(59, 130, 246, 0.3)",
        transition: "all 0.3s ease",
        marginBottom: "0.625rem",
      },

      userProfileName: {
        fontSize: isMobile ? "1.125rem" : "1.25rem",
        fontWeight: "700",
        color: c.text,
        textAlign: "center",
        marginBottom: "0.375rem",
      },

      userProfileStatus: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
        marginTop: "0.75rem",
      },

      userProfileStatusDot: {
        width: "0.625rem",
        height: "0.625rem",
        borderRadius: "50%",
        background: c.statusOnline,
      },

      userProfileStatusText: {
        fontSize: fontSizes.small,
        color: c.textSecondary,
        fontWeight: "500",
      },

      userProfileContent: {
        padding: "1rem 0",
      },

      userProfileSection: {
        padding: isMobile ? "0.75rem 1.25rem" : "0.75rem 1.5rem",
      },

      userProfileSectionTitle: {
        fontSize: fontSizes.small,
        fontWeight: "600",
        color: c.textSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.03125rem",
        marginBottom: "0.75rem",
      },

      userProfileItem: {
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.625rem 0",
        fontSize: fontSizes.body,
        color: c.text,
      },

      userProfileItemIcon: {
        color: "#2e7cba",
        flexShrink: 0,
      },

      logoutBtn: {
        background: "linear-gradient(135deg, #ef4444, #dc2626)",
        color: "white",
        border: "none",
        padding: isMobile ? "0.75rem 1rem" : "0.75rem 0.955rem",
        borderRadius: isMobile ? "0.75rem" : "9.875rem",
        cursor: "pointer",
        fontSize: fontSizes.body,
        fontWeight: "600",
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        transition: "all 0.3s ease",
//        boxShadow: "0 0.375rem 1.25rem rgba(239, 68, 68, 0.3)",
        height: isMobile ? "2.75rem" : "2.85rem",
      },
      switchbtn: {
        background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
        color: "white",
        border: "none",
        padding: isMobile ? "0.75rem 1rem" : "0.75rem 0.955rem",
        borderRadius: isMobile ? "0.75rem" : "9.875rem",
        cursor: "pointer",
        fontSize: fontSizes.body,
        fontWeight: "600",
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
      },

      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.4)",
        zIndex: 999,
        opacity: sidebarOpen && isMobile ? 1 : 0,
        visibility: sidebarOpen && isMobile ? "visible" : "hidden",
        transition: "all 0.3s ease",
        backdropFilter: "blur(0.25rem)",
      },

      commentModalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: c.modalOverlay,
        zIndex: 1002,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: showCommentModal ? 1 : 0,
        visibility: showCommentModal ? "visible" : "hidden",
        transition: "all 0.3s ease",
        backdropFilter: "blur(0.5rem)",
        padding: isMobile ? "1rem" : "2rem",
      },

      commentModal: {
        background: c.modalBg,
        backdropFilter: "blur(1.25rem)",
        border: `1px solid ${c.sidebarBorder}`,
        borderRadius: isMobile ? "1rem" : "1.25rem",
        padding: isMobile ? "1.25rem" : "1.75rem",
        width: "100%",
        maxWidth: isMobile ? "100%" : "32.5rem",
        boxShadow: c.notificationShadow,
        transform: showCommentModal ? "scale(1)" : "scale(0.95)",
        transition: "all 0.3s ease",
        maxHeight: isMobile ? "90vh" : "none",
        overflowY: isMobile ? "auto" : "visible",
        direction: isRTL ? "rtl" : "ltr",
      },

      commentModalHeader: {
        marginBottom: "1.25rem",
      },

      commentModalTitle: {
        fontSize: isMobile ? "1.125rem" : "1.25rem",
        fontWeight: "700",
        color: c.text,
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
      },

      commentModalContent: {
        marginBottom: "1.25rem",
      },

      commentTextarea: {
        width: "100%",
        minHeight: isMobile ? "6rem" : "7.5rem",
        background: c.inputBg,
        border: `1px solid ${c.inputBorder}`,
        borderRadius: isMobile ? "0.75rem" : "0.875rem",
        padding: isMobile ? "0.75rem" : "1rem",
        fontSize: fontSizes.body,
        color: c.text,
        outline: "none",
        resize: "vertical",
        transition: "all 0.3s ease",
        fontFamily: "inherit",
        lineHeight: "1.5",
      },

      commentModalActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: isMobile ? "0.75rem" : "0.875rem",
      },

      commentCancelBtn: {
        background: "transparent",
        border: `1px solid ${c.sidebarBorder}`,
        color: c.textSecondary,
        padding: isMobile ? "0.75rem 1rem" : "0.75rem 1.25rem",
        borderRadius: isMobile ? "0.75rem" : "0.875rem",
        cursor: "pointer",
        fontSize: fontSizes.body,
        fontWeight: "500",
        transition: "all 0.3s ease",
      },

      commentSubmitBtn: {
        background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
        color: "white",
        border: "none",
        padding: isMobile ? "0.75rem 1rem" : "0.75rem 1.25rem",
        borderRadius: isMobile ? "0.75rem" : "0.875rem",
        cursor: "pointer",
        fontSize: fontSizes.body,
        fontWeight: "600",
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
        transition: "all 0.3s ease",
        boxShadow: "0 0.375rem 1.25rem rgba(59, 130, 246, 0.3)",
      },

      existingComments: {
        marginTop: "1.25rem",
        maxHeight: isMobile ? "10rem" : "13.75rem",
        overflowY: "auto",
      },

      commentItem: {
        background: c.inputBg,
        border: `1px solid ${c.sidebarBorder}`,
        borderRadius: "0.75rem",
        padding: isMobile ? "0.75rem" : "1rem",
        marginBottom: "0.75rem",
      },

      commentAuthor: {
        fontSize: fontSizes.small,
        fontWeight: "600",
        color: "#2e7cba",
        marginBottom: "0.375rem",
      },

      commentText: {
        fontSize: fontSizes.body,
        color: c.text,
        marginBottom: "0.375rem",
        lineHeight: "1.5",
      },

      commentTimestamp: {
        fontSize: fontSizes.tiny,
        color: c.textSecondary,
      },
    };
  };

  // ===== GET USER INITIALS =====
  const getUserInitials = (name) => {
    if (!name) return "U";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (
      words[0].charAt(0) + words[words.length - 1].charAt(0)
    ).toUpperCase();
  };
  const styles = getResponsiveStyles();

  const getNotificationIconStyle = (type) => {
    const colors = {
      info: { background: "rgba(59, 130, 246, 0.15)", color: "#2e7cba" },
      success: { background: "rgba(34, 197, 94, 0.15)", color: "#22c55e" },
      warning: { background: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" },
      error: { background: "rgba(239, 68, 68, 0.15)", color: "#ef4444" },
    };
    return { ...styles.notificationIcon, ...colors[type] };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "online":
        return c.statusOnline;
      case "offline":
        return c.statusOffline;
      case "away":
        return c.statusAway;
      case "busy":
        return c.statusBusy;
      default:
        return c.statusOffline;
    }
  };

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100vh" }}
      className={theme}
    >
      {/* ===== GLOBAL STYLES ===== */}
      <style>
        {`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: transparent !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        #root {
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: transparent !important;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.8; }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(0.625rem); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        .logo:hover {
          transform: scale(${
            isMobile ? "1.05" : "1.1"
          }) rotate(5deg) !important;
          box-shadow: 0 0 1.5625rem rgba(59, 130, 246, 0.6) !important;
        }
        
        .infinity-title {
          animation: gradientShift 3s ease infinite;
          background-size: 200% 200%;
        }
        
        .infinity-title:hover {
          transform: scale(${isMobile ? "1.02" : "1.05"}) !important;
          filter: drop-shadow(0 0 0.625rem rgba(59, 130, 246, 0.5)) !important;
        }
        
        .menu-btn:hover {
          background: ${c.navHover} !important;
          transform: scale(${isMobile ? "1.05" : "1.1"}) !important;
        }

        .collapse-btn:hover {
          background: ${c.navHover} !important;
          transform: scale(1.1) !important;
          box-shadow: 0 0.25rem 0.75rem rgba(59, 130, 246, 0.2) !important;
        }
        
        .theme-toggle:hover {
          background: ${c.navHover} !important;
          transform: scale(${isMobile ? "1.05" : "1.1"}) !important;
        }
        
        .notification-btn:hover {
          background: ${c.navHover} !important;
          transform: scale(${isMobile ? "1.05" : "1.1"}) !important;
        }
        
        .notification-item:hover {
          background: ${c.navHover} !important;
          transform: translateX(${
            isMobile ? "0.125rem" : "0.25rem"
          }) !important;
        }
        
        .user-info:hover {
          background: ${c.navHover} !important;
          transform: scale(${isMobile ? "1.01" : "1.02"}) !important;
        }
        
        .user-profile-action:hover {
          background: ${c.navHover} !important;
          transform: translateX(${
            isMobile ? "0.125rem" : "0.25rem"
          }) !important;
        }

        .mark-all-read-btn:hover {
          transform: translateY(-0.125rem) scale(${
            isMobile ? "1.02" : "1.05"
          }) !important;
          box-shadow: 0 0.25rem 0.75rem rgba(59, 130, 246, 0.4) !important;
        }

        .mark-all-read-btn:active {
          transform: translateY(0rem) scale(1.02) !important;
        }
        
        .logout-btn:hover {
          transform: translateY(-0.1875rem) scale(${
            isMobile ? "1.02" : "1.05"
          }) !important;
          box-shadow: 0 0.5rem 1.5625rem rgba(239, 68, 68, 0.4) !important;
        }
        
        .nav-item:hover {
          background: ${c.navHover} !important;
          color: ${c.text} !important;
          transform: ${
            isCollapsed && !isMobile
              ? "scale(1.05)"
              : `translateX(${isMobile ? "0.25rem" : "0.5rem"}) scale(${
                  isMobile ? "1.01" : "1.02"
                })`
          } !important;
        }

        .unread-dot:hover {
          transform: scale(${isMobile ? "1.2" : "1.3"}) !important;
          box-shadow: 0 0.375rem 0.9375rem rgba(239, 68, 68, 0.6) !important;
        }

        .comment-textarea:focus {
          border-color: #2e7cba !important;
          box-shadow: 0 0 0 0.25rem rgba(59, 130, 246, 0.15) !important;
        }
        
        .comment-cancel-btn:hover {
          background: ${c.navHover} !important;
          color: ${c.text} !important;
          transform: scale(${isMobile ? "1.02" : "1.05"}) !important;
        }
        
        .comment-submit-btn:hover {
          transform: translateY(-0.125rem) scale(${
            isMobile ? "1.02" : "1.05"
          }) !important;
          box-shadow: 0 0.5rem 1.5625rem rgba(59, 130, 246, 0.4) !important;
        }
        
        .comment-submit-btn:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
          transform: none !important;
        }

        .notification-list::-webkit-scrollbar,
        .existing-comments::-webkit-scrollbar,
        .nav::-webkit-scrollbar,
        .content::-webkit-scrollbar,
        .user-profile-dropdown::-webkit-scrollbar {
          width: ${isMobile ? "4px" : isTablet ? "6px" : "8px"};
          height: ${isMobile ? "4px" : isTablet ? "6px" : "8px"};
        }

                  /* ===== ????? scrollbar ?? ?????? ??? ===== */
.nav::-webkit-scrollbar {
  display: none !important;
}

.nav {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE and Edge */
}

/* ????? scrollbar ?? ???? ??????? ??? ???? */
.notification-list::-webkit-scrollbar,
.existing-comments::-webkit-scrollbar,
.content::-webkit-scrollbar,
.user-profile-dropdown::-webkit-scrollbar {
  display: none !important;
}

.notification-list,
.existing-comments,
.content,
.user-profile-dropdown {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
        
        .notification-list::-webkit-scrollbar-track,
        .existing-comments::-webkit-scrollbar-track,
        .nav::-webkit-scrollbar-track,
        .content::-webkit-scrollbar-track,
        .user-profile-dropdown::-webkit-scrollbar-track {
          background: ${c.scrollTrack};
          border-radius: ${isMobile ? "6px" : "12px"};
          margin: ${isMobile ? "2px" : "4px"} 0;
        }
        
        .notification-list::-webkit-scrollbar-thumb,
        .existing-comments::-webkit-scrollbar-thumb,
        .nav::-webkit-scrollbar-thumb,
        .content::-webkit-scrollbar-thumb,
        .user-profile-dropdown::-webkit-scrollbar-thumb {
          background: ${c.scrollThumb};
          border-radius: ${isMobile ? "6px" : "12px"};
          border: ${isMobile ? "1px" : "2px"} solid transparent;
          background-clip: padding-box;
          transition: all 0.3s ease;
          box-shadow: 0 ${isMobile ? "1px" : "2px"} ${
          isMobile ? "4px" : "8px"
        } rgba(0, 0, 0, 0.1);
        }
        
        .notification-list::-webkit-scrollbar-thumb:hover,
        .existing-comments::-webkit-scrollbar-thumb:hover,
        .nav::-webkit-scrollbar-thumb:hover,
        .content::-webkit-scrollbar-thumb:hover,
        .user-profile-dropdown::-webkit-scrollbar-thumb:hover {
          background: ${c.scrollThumbHover};
          transform: scaleX(${isMobile ? "1.05" : "1.1"});
          box-shadow: 0 ${isMobile ? "2px" : "4px"} ${
          isMobile ? "6px" : "12px"
        } rgba(0, 0, 0, 0.15);
        }
        
        .notification-list::-webkit-scrollbar-thumb:active,
        .existing-comments::-webkit-scrollbar-thumb:active,
        .nav::-webkit-scrollbar-thumb:active,
        .content::-webkit-scrollbar-thumb:active,
        .user-profile-dropdown::-webkit-scrollbar-thumb:active {
          background: ${c.scrollThumbActive};
          transform: scaleX(${isMobile ? "1.1" : "1.2"});
          box-shadow: 0 ${isMobile ? "3px" : "6px"} ${
          isMobile ? "8px" : "16px"
        } rgba(0, 0, 0, 0.2);
        }

        .notification-list::-webkit-scrollbar-corner,
        .existing-comments::-webkit-scrollbar-corner,
        .nav::-webkit-scrollbar-corner,
        .content::-webkit-scrollbar-corner,
        .user-profile-dropdown::-webkit-scrollbar-corner {
          background: ${c.scrollTrack};
        }

        .notification-list,
        .existing-comments,
        .nav,
        .content,
        .user-profile-dropdown {
          scrollbar-width: ${isMobile ? "thin" : "thin"};
          scrollbar-color: ${
            theme === "light"
              ? "rgba(59, 130, 246, 0.3) rgba(249, 250, 251, 0.8)"
              : "rgba(99, 102, 241, 0.4) rgba(30, 41, 59, 0.6)"
          };
        }

        @media (max-width: 480px) {
          .user-details {
            display: none !important;
          }
          .date-time-display {
            display: none !important;
          }
          .logout-text {
            display: none !important;
          }
          .user-dropdown-icon {
            display: none !important;
          }
        }

        @media (min-width: 481px) and (max-width: 768px) {
          .user-details {
            display: none !important;
          }
          .date-time-display {
            display: none !important;
          }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .date-time-display {
            display: flex !important;
          }
          .user-details {
            display: flex !important;
          }
        }

        .fade-in {
          animation: fadeIn 0.4s ease-out;
        }

        .slide-in-left {
          animation: slideInLeft 0.4s ease-out;
        }

        @media (pointer: coarse) {
          button, .nav-item, .notification-item, .user-info {
            min-height: 2.75rem !important;
            min-width: 2.75rem !important;
          }
        }

        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .logo img, .user-avatar, .user-profile-avatar {
            image-rendering: -webkit-optimize-contrast;
            image-rendering: crisp-edges;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        @media (prefers-color-scheme: dark) {
          :root {
            color-scheme: dark;
          }
        }

        @media (prefers-color-scheme: light) {
          :root {
            color-scheme: light;
          }
        }
          .profile-upload-btn:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6) !important;
}

.profile-upload-btn:active {
  transform: scale(0.95) !important;
}

.profile-upload-btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
}
      `}
      </style>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(10px)",
            padding: isMobile ? "20px" : "40px",
          }}
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            style={{
              background: theme === "light"
                ? "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)"
                : "linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)",
              border: theme === "light"
                ? "2px solid rgba(226, 232, 240, 0.8)"
                : "2px solid rgba(71, 85, 105, 0.4)",
              borderRadius: isMobile ? "20px" : "28px",
              padding: isMobile ? "24px" : "40px",
              maxWidth: "600px",
              width: "100%",
              backdropFilter: "blur(24px)",
              boxShadow: theme === "light"
                ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                : "0 25px 50px -12px rgba(0, 0, 0, 0.7)",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              flexDirection: isMobile ? "column" : "row",
            }}>
              <button
                onClick={completeLogout}
                style={{
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "14px",
                  padding: isMobile ? "14px 28px" : "12px 32px",
                  fontWeight: "700",
                  fontSize: isMobile ? "15px" : "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  flex: isMobile ? "none" : 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {isRTL ? "????? ??????" : "Confirm Logout"}
              </button>

              <button
                onClick={() => setShowLogoutModal(false)}
                style={{
                  background: theme === "light"
                    ? "rgba(226, 232, 240, 0.8)"
                    : "rgba(71, 85, 105, 0.5)",
                  color: theme === "light" ? "#1e293b" : "#ffffff",
                  border: "none",
                  borderRadius: "14px",
                  padding: isMobile ? "14px 28px" : "12px 32px",
                  fontWeight: "700",
                  fontSize: isMobile ? "15px" : "16px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  flex: isMobile ? "none" : 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
              >
                {isRTL ? "?????" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.wrapper}>
        {/* ===== MOBILE OVERLAY ===== */}
        <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />

        {/* ===== COMMENT MODAL ===== */}
        <div style={styles.commentModalOverlay}>
          <div
            ref={commentModalRef}
            style={styles.commentModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.commentModalHeader}>
              <div style={styles.commentModalTitle}>
                <MessageSquare size={isMobile ? 20 : 24} />
                Add Comment
              </div>
            </div>

            <div style={styles.commentModalContent}>
              {selectedNotification && (
                <div
                  style={{
                    background: c.inputBg,
                    border: `1px solid ${c.sidebarBorder}`,
                    borderRadius: isMobile ? "0.75rem" : "0.875rem",
                    padding: isMobile ? "0.75rem" : "1rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "0.875rem" : "1rem",
                      fontWeight: "600",
                      color: c.text,
                      marginBottom: "0.375rem",
                    }}
                  >
                    {selectedNotification.title}
                  </div>
                  <div
                    style={{
                      fontSize: fontSizes.small,
                      color: c.textSecondary,
                    }}
                  >
                    {selectedNotification.message}
                  </div>
                </div>
              )}

              <textarea
                style={styles.commentTextarea}
                className="comment-textarea"
                placeholder="Write your comment here... (Press Enter to submit, Shift+Enter for new line)"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={handleCommentKeyPress}
              />

              {selectedNotification &&
                notificationComments[selectedNotification.id] && (
                  <div
                    style={styles.existingComments}
                    className="existing-comments"
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "0.875rem" : "1rem",
                        fontWeight: "600",
                        color: c.text,
                        marginBottom: "0.75rem",
                      }}
                    >
                      Previous Comments:
                    </div>
                    {notificationComments[selectedNotification.id].map(
                      (comment) => (
                        <div key={comment.id} style={styles.commentItem}>
                          <div style={styles.commentAuthor}>
                            {comment.author}
                          </div>
                          <div style={styles.commentText}>{comment.text}</div>
                          <div style={styles.commentTimestamp}>
                            {new Date(comment.timestamp).toLocaleString()}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
            </div>

            <div style={styles.commentModalActions}>
              <button
                style={styles.commentCancelBtn}
                className="comment-cancel-btn"
                onClick={() => {
                  setShowCommentModal(false);
                  setSelectedNotification(null);
                  setCommentText("");
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.commentSubmitBtn,
                  ...(commentText.trim() === ""
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}),
                }}
                className="comment-submit-btn"
                onClick={handleCommentSubmit}
                disabled={commentText.trim() === ""}
              >
                <Send size={isMobile ? 16 : 18} />
                Send Comment
              </button>
            </div>
          </div>
        </div>

        {/* ===== LEAVE REQUEST MODAL ===== */}
        <LeaveRequestModal
          isOpen={showLeaveModal}
          onClose={() => {
            setShowLeaveModal(false);
            setSelectedLeaveNotification(null);
          }}
          notification={selectedLeaveNotification}
          theme={theme}
          colors={colors}
        />

        {/* ===== SIDEBAR ===== */}
        <div ref={sidebarRef} style={styles.sidebar} className="slide-in-left">
          <div style={styles.sidebarHeader}>
            <Link style={{ textDecoration: "none", pointerEvents: "none" }}>
              <div
                style={{
                  ...styles.logo,
                  pointerEvents: "none",
                  cursor: "default",
                }}
              >
                <img
                  src={theme === "dark" ? logo1 : logo}
                  alt="Logo"
                  style={{
                    width: "140%",
                    height: "140%",
                    objectFit: "cover",
                    left: -10,
                  }}
                />
              </div>
            </Link>
            {(!isCollapsed || isMobile) && (
              <Link style={{ textDecoration: "none", pointerEvents: "none" }}>
                <h2
                  style={{
                    ...styles.sidebarTitle,
                    pointerEvents: "none",
                    cursor: "default",
                  }}
                  className="infinity-title"
                >
                  NexPloy
                </h2>
              </Link>
            )}
          </div>

          <nav style={styles.nav} className="nav">
            {navItems.map((item) => (
              <Tooltip
                key={item.page}
                text={isCollapsed && !isMobile ? t[item.page] || item.text : ""}
                show={isCollapsed && !isMobile && hoveredNavItem === item.page}
              >
                <div
                  style={{
                    ...styles.navItem,
                    ...(page === item.page ? styles.navItemActive : {}),
                  }}
                  className="nav-item"
                  onClick={() => {
                    if (item.locked) {
                      const title = (t && t.notice) || "Notice";
                      const message = item.lockMessage || undefined;
                      showModal({
                        title,
                        message,
                        options: {
                          requestAccessEndpoint: process.env.REACT_APP_BASE_URL
                            ? `${process.env.REACT_APP_BASE_URL}/api/request-module-access`
                            : null,
                          module: item.page,
                        },
                      });
                      return;
                    }
                    setPage(item.page);
                  }}
                  // onMouseEnter={() => setHoveredNavItem(item.page)}
                  // onMouseLeave={() => setHoveredNavItem(null)}
                >
                  <item.icon size={20} />
                  {!isCollapsed && (
                    <span style={styles.navItemText}>
                      {t[item.page] || item.text}
                      {item.locked && " ??"}
                    </span>
                  )}
                </div>
              </Tooltip>
            ))}
          </nav>

          {/* ===== SIDEBAR FOOTER ===== */}
          {/* <div
                            style={{
                              padding: isCollapsed && !isMobile ? "0.75rem" : spacing.padding,
                              borderTop: `1px solid ${c.sidebarBorder}`,
                              textAlign: "center",
                              transition: "all 0.3s ease"
                            }}
                          >
                            {(!isCollapsed || isMobile) && (
                             
                                <div
                                  style={{
                                    color: c.textSecondary,
                                    fontSize: fontSizes.small,
                                    marginBottom: "0.5rem"
                                  }}
                                >
                                  {t.poweredBy}
                                </div>
                                <div
                                  style={{
                                    color: "#3b85f6",
                                    fontSize: fontSizes.small,
                                    fontWeight: "bold",
                                    textDecoration: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "0.25rem",
                                    cursor: "default"
                                  }}
                                >
                                  Infinity Business Solutions
                                </div>
                             
                            )}
                          </div> */}
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div style={styles.main}>
          {/* ===== TOPBAR ===== */}
          <div style={styles.topbar}>
            <div style={styles.topbarLeft}>
              <button
                style={styles.menuBtn}
                className="menu-btn"
                onClick={handleSidebarToggle}
                title={
                  isMobile
                    ? sidebarOpen
                      ? "Close Sidebar"
                      : "Open Sidebar"
                    : isCollapsed
                    ? "Expand Sidebar"
                    : "Collapse Sidebar"
                }
              >
                {isMobile ? (
                  sidebarOpen ? (
                    <X size={20} />
                  ) : (
                    <Menu size={20} />
                  )
                ) : isRTL ? (
                  isCollapsed ? (
                    <ChevronLeft size={isMobile ? 20 : 24} />
                  ) : (
                    <ChevronRight size={isMobile ? 20 : 24} />
                  )
                ) : isCollapsed ? (
                  <ChevronRight size={isMobile ? 20 : 24} />
                ) : (
                  <ChevronLeft size={isMobile ? 20 : 24} />
                )}
              </button>

              {/* <div style={styles.dateTimeDisplay} className="date-time-display fade-in">
                <Calendar size={20} style={styles.dateTimeIcon} />
                <span>{formatDateTime(currentDateTime)}</span>
              </div> */}
            </div>

            <div style={styles.topbarRight}>
              {isRTL ? (
                <>
                  {/* ????? ??????: User ? Bell ? Theme ? Manager */}

                  {/* ===== USER PROFILE ===== */}
                  <div ref={userProfileRef} style={{ position: "relative" }}>
                    <div
                      style={styles.userInfo}
                      className="user-info"
                      onClick={toggleUserProfile}
                    >
                      <div style={{ position: "relative" }}>
                        {profilePic ? (
                          <img
                            src={userData.avatar}
                            alt="User Avatar"
                            style={styles.userAvatar}
                          />
                        ) : (
                          <div
                            style={{
                              ...styles.userAvatar,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background:
                                "linear-gradient(135deg, #2e7cba, #2e7cba)",
                              color: "white",
                              fontSize: isMobile ? "0.875rem" : "1.125rem",
                              fontWeight: "700",
                              textTransform: "uppercase",
                            }}
                          >
                            {getUserInitials(userData.name)}
                          </div>
                        )}
                        <div
                          style={{
                            ...styles.userAvatarStatus,
                            background: getStatusColor(userData.status),
                          }}
                        />
                      </div>
                      <div style={styles.userDetails}>
                        <div style={styles.userName}>
                          {userData.name ? userData.name.split(" ")[0] : "User"}
                        </div>
                      </div>
                      <ChevronDown
                        size={isMobile ? 16 : 18}
                        style={styles.userDropdownIcon}
                        className="user-dropdown-icon"
                      />
                    </div>

                    <div
                      style={styles.userProfileDropdown}
                      className="user-profile-dropdown"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={styles.userProfileHeader}>
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          {profilePic ? (
                            <img
                              src={userData.avatar}
                              alt="User Avatar"
                              style={styles.userProfileAvatar}
                            />
                          ) : (
                            <div
                              style={{
                                ...styles.userProfileAvatar,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #2e7cba, #2e7cba)",
                                color: "white",
                                fontSize: isMobile ? "1.75rem" : "2.25rem",
                                fontWeight: "700",
                                textTransform: "uppercase",
                              }}
                            >
                              {getUserInitials(userData.name)}
                            </div>
                          )}

                          {/* Upload button overlay */}
                          <button
                            onClick={triggerProfilePicInput}
                            disabled={isUploadingProfilePic}
                            style={{
                              position: "absolute",
                              bottom: "0",
                              right: "0",
                              width: isMobile ? "2rem" : "2.25rem",
                              height: isMobile ? "2rem" : "2.25rem",
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #2e7cba, #2e7cba)",
                              border: "3px solid white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: isUploadingProfilePic
                                ? "not-allowed"
                                : "pointer",
                              transition: "all 0.3s ease",
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                              opacity: isUploadingProfilePic ? 0.6 : 1,
                              color: "white",
                              zIndex: 1,
                            }}
                            className="profile-upload-btn"
                            title={
                              isUploadingProfilePic
                                ? "Uploading..."
                                : "Upload profile picture"
                            }
                          >
                            {isUploadingProfilePic ? (
                              <Clock size={isMobile ? 14 : 16} />
                            ) : (
                              <Camera size={isMobile ? 14 : 16} />
                            )}
                          </button>

                          {/* Hidden file input */}
                          <input
                            ref={profilePicInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleProfilePicSelect}
                            style={{ display: "none" }}
                          />

                          {/* Status indicator */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: isMobile ? "0.5rem" : "0.5rem",
                              left: isMobile ? "0.5rem" : "0.5rem",
                              width: isMobile ? "1rem" : "1.125rem",
                              height: isMobile ? "1rem" : "1.125rem",
                              borderRadius: "50%",
                              border: "3px solid white",
                              background: getStatusColor(userData.status),
                            }}
                          />
                        </div>

                        <div style={styles.userProfileName}>
                          {userData.name ||
                            (language === "ar" ? "??? ????????" : "User Name")}
                        </div>
                        <div style={styles.userProfileStatus}>
                          <div
                            style={{
                              ...styles.userProfileStatusDot,
                              background: getStatusColor(userData.status),
                            }}
                          />
                          <span style={styles.userProfileStatusText}>
                            {userData.status === "online"
                              ? t.activeNow
                              : userData.lastSeen}
                          </span>
                        </div>
                      </div>

                      <div style={styles.userProfileContent}>
                        <div style={styles.userProfileSection}>
                          <div style={styles.userProfileSectionTitle}>
                            Contact
                          </div>
                          <div style={styles.userProfileItem}>
                            <Mail
                              size={16}
                              style={styles.userProfileItemIcon}
                            />
                            <span>
                              {userData.email || t.noEmail || "No email"}
                            </span>
                          </div>
                          <div style={styles.userProfileItem}>
                            <Phone
                              size={16}
                              style={styles.userProfileItemIcon}
                            />
                            <span>
                              {userData.phone || t.noPhone || "No phone"}
                            </span>
                          </div>
                          <button
                            style={styles.logoutBtn}
                           // className="logout-btn"
                            onClick={handleLogoutClick}
                          >
                            <Power size={isMobile ? 16 : 18} />
                            <span className="logout-text">
                              {t.logout || "Logout"}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ===== NOTIFICATIONS ===== */}
                  <div ref={notificationRef} style={styles.notificationWrapper}>
                    <button
                      style={styles.notificationBtn}
                      className="notification-btn"
                      onClick={toggleNotifications}
                    >
                      <Bell size={isMobile ? 18 : 20} />
                      {unreadCount > 0 && (
                        <div style={styles.notificationBadge}>
                          {unreadCount}
                        </div>
                      )}
                    </button>

                    <div
                      style={styles.notificationDropdown}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={styles.notificationHeader}>
                        <div style={styles.notificationTitle}>{tr('notifications', language)}</div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                          }}
                        >
                          {notificationsLoading && (
                            <div
                              style={{
                                fontSize: fontSizes.small,
                                color: c.textSecondary,
                              }}
                            >
                              {tr('loading', language)}
                            </div>
                          )}
                          {unreadCount > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAllAsRead();
                              }}
                              style={styles.markAllReadBtn}
                              className="mark-all-read-btn"
                              title={tr('markAllReadTitle', language)}
                            >
                              <CheckCircle size={isMobile ? 12 : 14} />
                              {tr('markAllRead', language)}
                            </button>
                          )}
                        </div>
                      </div>
                      <div
                        style={styles.notificationList}
                        className="notification-list"
                      >
                        {notificationsError ? (
                          <div
                            style={{
                              padding: isMobile
                                ? "1rem 1.25rem"
                                : "1.25rem 1.5rem",
                              textAlign: "center",
                              color: "#ef4444",
                            }}
                          >
                            <AlertCircle
                              size={isMobile ? 20 : 24}
                              style={{ marginBottom: "0.5rem" }}
                            />
                            <div>{tr('failedToLoad', language)}</div>
                            <button
                              onClick={fetchNotifications}
                              style={{
                                marginTop: "0.5rem",
                                padding: "0.375rem 0.75rem",
                                background: "transparent",
                                border: `1px solid ${c.sidebarBorder}`,
                                borderRadius: "0.5rem",
                                color: c.text,
                                cursor: "pointer",
                              }}
                            >
                              Retry
                            </button>
                          </div>
                        ) : notificationData.length === 0 &&
                          !notificationsLoading ? (
                          <div
                            style={{
                              padding: isMobile
                                ? "1rem 1.25rem"
                                : "1.25rem 1.5rem",
                              textAlign: "center",
                              color: c.textSecondary,
                            }}
                          >
                            <Bell
                              size={isMobile ? 20 : 24}
                              style={{ marginBottom: "0.5rem" }}
                            />
                            <div>{tr('noNotifications', language)}</div>
                          </div>
                        ) : (
                          notificationData.map((notification) => {
                            const IconComponent = notification.icon;
                            const hasComments =
                              notificationComments[notification.id] &&
                              notificationComments[notification.id].length > 0;
                            return (
                              <div
                                key={notification.id}
                                style={styles.notificationItem}
                                className="notification-item"
                                onClick={(e) =>
                                  handleNotificationClick(e, notification.id)
                                }
                              >
                                <div
                                  style={getNotificationIconStyle(
                                    notification.type
                                  )}
                                >
                                  <IconComponent size={isMobile ? 16 : 18} />
                                </div>
                                <div style={styles.notificationContent}>
                                  {notification.type === 'EMPLOYEE_ACTION' ? (
                                    <div style={styles.notificationItemTitle}>
                                      {notification.title || notification.message}
                                    </div>
                                  ) : (
                                    <>
                                      <div style={styles.notificationItemTitle}>
                                        {notification.title}
                                      </div>
                                      <div style={styles.notificationItemMessage}>
                                        {notification.message}
                                      </div>
                                    </>
                                  )}
                                  {/* If this is a calendar invite, show quick RSVP buttons */}
                                  {notification.type === 'CALENDAR_EVENT' && notification.eventId && (
                                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }} onClick={(ev)=>ev.stopPropagation()}>
                                      <button
                                        onClick={(ev) => handleNotificationRsvp(ev, notification, 'accepted')}
                                        style={{ padding: '6px 8px', background: '#2e7c2e', color: 'white', border: 'none', borderRadius: 6 }}
                                      >{language === 'ar' ? 'قبول' : 'Accept'}</button>
                                      <button
                                        onClick={(ev) => handleNotificationRsvp(ev, notification, 'declined')}
                                        style={{ padding: '6px 8px', background: '#b00020', color: 'white', border: 'none', borderRadius: 6 }}
                                      >{language === 'ar' ? 'رفض' : 'Decline'}</button>
                                      <button
                                        onClick={(ev) => handleNotificationRsvp(ev, notification, 'going')}
                                        style={{ padding: '6px 8px', background: '#0b63d4', color: 'white', border: 'none', borderRadius: 6 }}
                                      >{language === 'ar' ? 'حاضر' : 'Going'}</button>
                                      <button
                                        onClick={(ev) => handleNotificationRsvp(ev, notification, 'tentative')}
                                        style={{ padding: '6px 8px', background: '#f0ad4e', color: 'white', border: 'none', borderRadius: 6 }}
                                      >{language === 'ar' ? 'ربما' : 'Maybe'}</button>
                                    </div>
                                  )}
                                 <div style={styles.notificationTime}>
  {formatTimeAgo(notification.createdAt, language)}
</div>
                                  {hasComments && (
                                    <div
                                      style={{
                                        fontSize: fontSizes.tiny,
                                        color: "#2e7cba",
                                        marginTop: "0.375rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.375rem",
                                      }}
                                    >
                                      <MessageSquare
                                        size={isMobile ? 12 : 14}
                                      />
                                      {
                                        notificationComments[notification.id]
                                          .length
                                      }{" "}
                                      comment
                                      {notificationComments[notification.id]
                                        .length !== 1
                                        ? "s"
                                        : ""}
                                    </div>
                                  )}
                                </div>
                                {!notification.read && (
                                  <div
                                    style={{
                                      ...styles.unreadDot,
                                      ...(notification.type ===
                                        "LEAVE_REQUEST" && {
                                        background:
                                          "linear-gradient(135deg, #f59e0b, #d97706)",
                                      }),
                                    }}
                                    className="unread-dot"
                                    onClick={(e) =>
                                      handleRedDotClick(e, notification)
                                    }
                                    title={
                                      notification.type === "leave_request"
                                        ? "Click to review leave request"
                                        : "Click to add a comment"
                                    }
                                  />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    style={styles.themeToggle}
                    className="theme-toggle"
                    onClick={toggleTheme}
                    title={`Switch to ${
                      theme === "light" ? "dark" : "light"
                    } mode`}
                  >
                    {theme === "light" ? (
                      <Moon size={isMobile ? 18 : 22} />
                    ) : (
                      <Sun size={isMobile ? 18 : 22} />
                    )}
                  </button>

                  {viewModeSwitcher}
                </>
              ) : (
                <>
                  {/* ????? ?????????: Bell ? Theme ? Manager ? User */}

                  {viewModeSwitcher}

                  <button
                    style={styles.themeToggle}
                    className="theme-toggle"
                    onClick={toggleTheme}
                    title={`Switch to ${
                      theme === "light" ? "dark" : "light"
                    } mode`}
                  >
                    {theme === "light" ? (
                      <Moon size={isMobile ? 18 : 22} />
                    ) : (
                      <Sun size={isMobile ? 18 : 22} />
                    )}
                  </button>
                  {/* ===== NOTIFICATIONS ===== */}
                  <div ref={notificationRef} style={styles.notificationWrapper}>
                    <button
                      style={styles.notificationBtn}
                      className="notification-btn"
                      onClick={toggleNotifications}
                    >
                      <Bell size={isMobile ? 18 : 20} />
                      {unreadCount > 0 && (
                        <div style={styles.notificationBadge}>
                          {unreadCount}
                        </div>
                      )}
                    </button>

                    <div
                      style={styles.notificationDropdown}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={styles.notificationHeader}>
                        <div style={styles.notificationTitle}>{tr('notifications', language)}</div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                          }}
                        >
                          {notificationsLoading && (
                              <div
                                style={{
                                  fontSize: fontSizes.small,
                                  color: c.textSecondary,
                                }}
                              >
                                {tr('loading', language)}
                              </div>
                          )}
                          {unreadCount > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAllAsRead();
                              }}
                              style={styles.markAllReadBtn}
                              className="mark-all-read-btn"
                              title={tr('markAllReadTitle', language)}
                            >
                              <CheckCircle size={isMobile ? 12 : 14} />
                              {tr('markAllRead', language)}
                            </button>
                          )}
                        </div>
                      </div>
                      <div
                        style={styles.notificationList}
                        className="notification-list"
                      >
                        {notificationsError ? (
                          <div
                            style={{
                              padding: isMobile
                                ? "1rem 1.25rem"
                                : "1.25rem 1.5rem",
                              textAlign: "center",
                              color: "#ef4444",
                            }}
                          >
                            <AlertCircle
                              size={isMobile ? 20 : 24}
                              style={{ marginBottom: "0.5rem" }}
                            />
                            <div>{tr('failedToLoad', language)}</div>
                            <button
                              onClick={fetchNotifications}
                              style={{
                                marginTop: "0.5rem",
                                padding: "0.375rem 0.75rem",
                                background: "transparent",
                                border: `1px solid ${c.sidebarBorder}`,
                                borderRadius: "0.5rem",
                                color: c.text,
                                cursor: "pointer",
                              }}
                            >
                              Retry
                            </button>
                          </div>
                        ) : notificationData.length === 0 &&
                          !notificationsLoading ? (
                          <div
                            style={{
                              padding: isMobile
                                ? "1rem 1.25rem"
                                : "1.25rem 1.5rem",
                              textAlign: "center",
                              color: c.textSecondary,
                            }}
                          >
                            <Bell
                              size={isMobile ? 20 : 24}
                              style={{ marginBottom: "0.5rem" }}
                            />
                            <div>{tr('noNotifications', language)}</div>
                          </div>
                        ) : (
                          notificationData.map((notification) => {
                            const IconComponent = notification.icon;
                            const hasComments =
                              notificationComments[notification.id] &&
                              notificationComments[notification.id].length > 0;
                            return (
                              <div
                                key={notification.id}
                                style={styles.notificationItem}
                                className="notification-item"
                                onClick={(e) =>
                                  handleNotificationClick(e, notification.id)
                                }
                              >
                                <div
                                  style={getNotificationIconStyle(
                                    notification.type
                                  )}
                                >
                                  <IconComponent size={isMobile ? 16 : 18} />
                                </div>
                                <div style={styles.notificationContent}>
                                  <div style={styles.notificationItemTitle}>
                                    {notification.title}
                                  </div>
                                  <div style={styles.notificationItemMessage}>
                                    {notification.message}
                                  </div>
                                  <div style={styles.notificationTime}>
                                    {notification.time}
                                  </div>
                                  {hasComments && (
                                    <div
                                      style={{
                                        fontSize: fontSizes.tiny,
                                        color: "#2e7cba",
                                        marginTop: "0.375rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.375rem",
                                      }}
                                    >
                                      <MessageSquare
                                        size={isMobile ? 12 : 14}
                                      />
                                      {
                                        notificationComments[notification.id]
                                          .length
                                      }{" "}
                                      comment
                                      {notificationComments[notification.id]
                                        .length !== 1
                                        ? "s"
                                        : ""}
                                    </div>
                                  )}
                                </div>
                                {!notification.read && (
                                  <div
                                    style={{
                                      ...styles.unreadDot,
                                      ...(notification.type ===
                                        "LEAVE_REQUEST" && {
                                        background:
                                          "linear-gradient(135deg, #f59e0b, #d97706)",
                                      }),
                                    }}
                                    className="unread-dot"
                                    onClick={(e) =>
                                      handleRedDotClick(e, notification)
                                    }
                                    title={
                                      notification.type === "leave_request"
                                        ? "Click to review leave request"
                                        : "Click to add a comment"
                                    }
                                  />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ===== USER PROFILE ===== */}
                  <div ref={userProfileRef} style={{ position: "relative" }}>
                    <div
                      style={styles.userInfo}
                      className="user-info"
                      onClick={toggleUserProfile}
                    >
                      <div style={{ position: "relative" }}>
                        {profilePic ? (
                          <img
                            src={userData.avatar}
                            alt="User Avatar"
                            style={styles.userAvatar}
                          />
                        ) : (
                          <div
                            style={{
                              ...styles.userAvatar,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background:
                                "linear-gradient(135deg, #2e7cba, #2e7cba)",
                              color: "white",
                              fontSize: isMobile ? "0.875rem" : "1.125rem",
                              fontWeight: "700",
                              textTransform: "uppercase",
                            }}
                          >
                            {getUserInitials(userData.name)}
                          </div>
                        )}
                        <div
                          style={{
                            ...styles.userAvatarStatus,
                            background: getStatusColor(userData.status),
                          }}
                        />
                      </div>
                      <div style={styles.userDetails}>
                        <div style={styles.userName}>
                          {userData.name ? userData.name.split(" ")[0] : "User"}
                        </div>
                      </div>
                      <ChevronDown
                        size={isMobile ? 16 : 18}
                        style={styles.userDropdownIcon}
                        className="user-dropdown-icon"
                      />
                    </div>

                    <div
                      style={styles.userProfileDropdown}
                      className="user-profile-dropdown"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={styles.userProfileHeader}>
                        <div
                          style={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          {profilePic ? (
                            <img
                              src={userData.avatar}
                              alt="User Avatar"
                              style={styles.userProfileAvatar}
                            />
                          ) : (
                            <div
                              style={{
                                ...styles.userProfileAvatar,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #2e7cba, #2e7cba)",
                                color: "white",
                                fontSize: isMobile ? "1.75rem" : "2.25rem",
                                fontWeight: "700",
                                textTransform: "uppercase",
                              }}
                            >
                              {getUserInitials(userData.name)}
                            </div>
                          )}

                          {/* Upload button overlay */}
                          <button
                            onClick={triggerProfilePicInput}
                            disabled={isUploadingProfilePic}
                            style={{
                              position: "absolute",
                              bottom: "0",
                              right: "0",
                              width: isMobile ? "2rem" : "2.25rem",
                              height: isMobile ? "2rem" : "2.25rem",
                              borderRadius: "50%",
                              background:
                                "linear-gradient(135deg, #2e7cba, #2e7cba)",
                              border: "3px solid white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: isUploadingProfilePic
                                ? "not-allowed"
                                : "pointer",
                              transition: "all 0.3s ease",
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                              opacity: isUploadingProfilePic ? 0.6 : 1,
                              color: "white",
                              zIndex: 1,
                            }}
                            className="profile-upload-btn"
                            title={
                              isUploadingProfilePic
                                ? "Uploading..."
                                : "Upload profile picture"
                            }
                          >
                            {isUploadingProfilePic ? (
                              <Clock size={isMobile ? 14 : 16} />
                            ) : (
                              <Camera size={isMobile ? 14 : 16} />
                            )}
                          </button>

                          {/* Hidden file input */}
                          <input
                            ref={profilePicInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleProfilePicSelect}
                            style={{ display: "none" }}
                          />

                          {/* Status indicator */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: isMobile ? "0.5rem" : "0.5rem",
                              left: isMobile ? "0.5rem" : "0.5rem",
                              width: isMobile ? "1rem" : "1.125rem",
                              height: isMobile ? "1rem" : "1.125rem",
                              borderRadius: "50%",
                              border: "3px solid white",
                              background: getStatusColor(userData.status),
                            }}
                          />
                        </div>

                        <div style={styles.userProfileName}>
                          {userData.name || "User Name"}
                        </div>
                        <div style={styles.userProfileStatus}>
                          <div
                            style={{
                              ...styles.userProfileStatusDot,
                              background: getStatusColor(userData.status),
                            }}
                          />
                          <span style={styles.userProfileStatusText}>
                            {userData.status === "online"
                              ? "Active now"
                              : userData.lastSeen}
                          </span>
                        </div>
                      </div>

                      <div style={styles.userProfileContent}>
                        <div style={styles.userProfileSection}>
                          <div style={styles.userProfileSectionTitle}>
                            Contact
                          </div>
                          <div style={styles.userProfileItem}>
                            <Mail
                              size={16}
                              style={styles.userProfileItemIcon}
                            />
                            <span>{userData.email || "No email"}</span>
                          </div>
                          <div style={styles.userProfileItem}>
                            <Phone
                              size={16}
                              style={styles.userProfileItemIcon}
                            />
                            <span>{userData.phone || "No phone"}</span>
                          </div>
                          <button
                            style={styles.logoutBtn}
                            className="logout-btn"
                            onClick={handleLogoutClick}
                          >
                            <Power size={isMobile ? 16 : 18} />
                            <span className="logout-text">Logout</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ===== CONTENT ===== */}
          <div style={styles.content} className="fade-in content">
            {children}
          </div>
        </div>
      </div>
      {/* ===== CALENDAR EVENT MODAL ===== */}
{showCalendarEventModal && selectedCalendarEvent && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.6)",
      zIndex: 1003,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(8px)",
      padding: isMobile ? "1rem" : "2rem",
    }}
    onClick={() => {
      setShowCalendarEventModal(false);
      setSelectedCalendarEvent(null);
    }}
  >
    <div
      style={{
        background: c.modalBg,
        backdropFilter: "blur(20px)",
        border: `1px solid ${c.sidebarBorder}`,
        borderRadius: isMobile ? "1.25rem" : "1.5rem",
        padding: isMobile ? "1.5rem" : "2rem",
        width: "100%",
        maxWidth: isMobile ? "100%" : "500px",
        boxShadow: "0 25px 70px rgba(0, 0, 0, 0.3)",
        maxHeight: isMobile ? "85vh" : "90vh",
        overflowY: "auto",
        direction: isRTL ? "rtl" : "ltr",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1.5rem",
          paddingBottom: "1rem",
          borderBottom: `2px solid ${c.sidebarBorder}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <div
            style={{
              background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
              padding: "0.75rem",
              borderRadius: "0.875rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Calendar size={24} style={{ color: "white" }} />
          </div>
          <h2
            style={{
              fontSize: isMobile ? "1.25rem" : "1.5rem",
              fontWeight: "700",
              color: c.text,
              margin: 0,
            }}
          >
            {t.eventInvitation}
          </h2>
        </div>
        <button
          onClick={() => {
            setShowCalendarEventModal(false);
            setSelectedCalendarEvent(null);
          }}
          style={{
            background: "transparent",
            border: "none",
            color: c.textSecondary,
            cursor: "pointer",
            padding: "0.5rem",
            borderRadius: "0.5rem",
            transition: "all 0.2s ease",
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Event Details */}
      <div
        style={{
          background: c.inputBg,
          border: `1px solid ${c.sidebarBorder}`,
          borderRadius: "1rem",
          padding: isMobile ? "1rem" : "1.25rem",
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            fontSize: isMobile ? "1.125rem" : "1.25rem",
            fontWeight: "600",
            color: c.text,
            marginBottom: "1rem",
          }}
        >
          {selectedCalendarEvent.title}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {/* Sender */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
            <User size={18} style={{ color: "#2e7cba" }} />
            <span style={{ fontSize: fontSizes.body, color: c.text }}>
              {t.from} <strong>{selectedCalendarEvent.fromName || selectedCalendarEvent.from || (language === "ar" ? "??????" : "User")}</strong>
            </span>
          </div>

          {/* Message */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.625rem" }}>
            <MessageSquare size={18} style={{ color: "#2e7cba", marginTop: "0.125rem" }} />
            <span
              style={{
                fontSize: fontSizes.body,
                color: c.textSecondary,
                lineHeight: "1.6",
              }}
            >
              {selectedCalendarEvent.message}
            </span>
          </div>

          {/* Time */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
            <Clock size={18} style={{ color: "#2e7cba" }} />
            <span style={{ fontSize: fontSizes.small, color: c.textSecondary }}>
              {formatTimeAgo(selectedCalendarEvent.createdAt, language)}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "0.75rem",
        }}
      >
        {/* Accept Button */}
        <button
          onClick={() => {
            console.log("Approved event:", selectedCalendarEvent.eventId);
            setShowCalendarEventModal(false);
            setSelectedCalendarEvent(null);
          }}
          style={{
            flex: 1,
            background: "linear-gradient(135deg, #22c55e, #16a34a)",
            color: "white",
            border: "none",
            padding: isMobile ? "0.875rem" : "1rem",
            borderRadius: "0.875rem",
            cursor: "pointer",
            fontSize: fontSizes.body,
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 15px rgba(34, 197, 94, 0.3)",
          }}
        >
          <CheckCircle size={18} />
          {t.accept}
        </button>

        {/* View Calendar Button */}
        <button
          onClick={() => {
            setShowCalendarEventModal(false);
            setSelectedCalendarEvent(null);
            setPage("calendar");
          }}
          style={{
            flex: 1,
            background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
            color: "white",
            border: "none",
            padding: isMobile ? "0.875rem" : "1rem",
            borderRadius: "0.875rem",
            cursor: "pointer",
            fontSize: fontSizes.body,
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 15px rgba(59, 130, 246, 0.3)",
          }}
        >
          <Calendar size={18} />
          {t.viewCalendar}
        </button>

        {/* Decline Button */}
        <button
          onClick={() => {
            console.log("Rejected event:", selectedCalendarEvent.eventId);
            setShowCalendarEventModal(false);
            setSelectedCalendarEvent(null);
          }}
          style={{
            flex: isMobile ? 1 : 0.5,
            background: "transparent",
            color: c.textSecondary,
            border: `1px solid ${c.sidebarBorder}`,
            padding: isMobile ? "0.875rem" : "1rem",
            borderRadius: "0.875rem",
            cursor: "pointer",
            fontSize: fontSizes.body,
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            transition: "all 0.3s ease",
          }}
        >
          <X size={18} />
          {t.decline}
        </button>
      </div>
    </div>
  </div>
)}
      {/* ===== LOGOUT VIDEO ===== */}
{/* {showLogoutVideo && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0, 0, 0, 0.95)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(10px)",
    }}
  >
    <video
      autoPlay
      onEnded={completeLogout}
      style={{
        maxWidth: "90%",
        maxHeight: "90%",
        objectFit: "contain",
      }}
    >
      <source src={logoutVideo} type="video/mp4" />
    </video>
  </div>
)} */}

      {/* ===== ALERT MODAL ===== */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={theme === 'dark' ? 'dark' : 'light'}
        language={language}
      />
    </div>
  );
};

export default SidebarWithTopbar;
